import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { resolveNavLinks } from "../../lib/navUtils";

export default function Header({ navigation, globals, hasPortfolio = true, hasTestimonials = true, enabledSections }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const navLinks = resolveNavLinks(navigation, enabledSections);
  const logoText = globals?.logo || "Hub";
  const logoSubtitle = globals?.subtitle;
  const ctaLabel = globals?.cta_label || "Agendar Consultoria";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-offwhite/80 backdrop-blur-md border-b border-fendi/60 py-0 shadow-sm"
          : "bg-transparent py-2"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <button
          data-testid="logo-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Ir para o topo da página"
          className={`group bg-transparent border-none cursor-pointer p-0 ${
            scrolled
              ? "py-2"
              : "bg-transparent py-2"
          }`}
        >
          <div className="flex items-baseline gap-2">
            <span className={`font-serif text-2xl tracking-tight transition-colors duration-500 ${scrolled ? "text-softblack" : "text-white"}`}>
              {logoText}<span className="text-champagne">.</span>
            </span>
            {logoSubtitle && (
              <span className={`hidden sm:inline-block text-[10px] tracking-[0.34em] uppercase font-medium transition-colors duration-500 ${scrolled ? "text-softblack/80" : "text-white/85"}`}>
                {logoSubtitle}
              </span>
            )}
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`link-underline text-[13px] font-medium tracking-wide transition-colors duration-500 ${
                scrolled 
                  ? "text-softblack/80 hover:text-softblack" 
                  : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="#contato" data-testid="cta-header-schedule" className={`hidden sm:inline-flex btn-primary !px-5 lg:!px-6 !py-3 !text-[10px] shadow-lg transition-all duration-500 ${!scrolled && "border-white/20 hover:bg-white hover:text-softblack"}`}>
            {ctaLabel}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors duration-500 ${scrolled ? "text-softblack" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-fendi/60 bg-offwhite/95 backdrop-blur-md animate-in slide-in-from-top-2 duration-300">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-softblack/85 text-sm tracking-wide py-2 border-b border-fendi/40"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
